import { Card, CardContent } from "@/components/ui/card";

import { DominoReorder } from "../components/DominoReorder";
import { KillToggle } from "../components/KillToggle";
import { Notice } from "../components/Notice";
import { QueryErrorState, TabHeading, TabSkeleton } from "../components/tab-states";
import { buildDominoRegistry, mergeDominoOrder } from "../data/cockpit-data";
import { useConfig, useStats } from "../data/queries";

/** Kill state, domino priority, and threshold edits. Changes apply next cycle. */
export function ConfigTab() {
  // The domino registry is derived from the stats section, so config depends on both.
  const config = useConfig();
  const stats = useStats();

  const configData = config.data;
  const dominoRegistry = buildDominoRegistry({ stats: stats.data ?? null });
  const dominoOrder = mergeDominoOrder(configData?.domino_order ?? null, dominoRegistry);

  return (
    <div className="flex flex-col gap-4">
      <TabHeading
        title="Configuration"
        description="Kill state, domino priority, and threshold edits. Changes apply next cycle."
      />
      {config.isPending || stats.isPending ? (
        <TabSkeleton />
      ) : config.isError ? (
        <QueryErrorState title="Configuration unavailable" onRetry={() => config.refetch()} />
      ) : !configData ? (
        <Notice title="Configuration unavailable">
          The machine config section is required for this tab.
        </Notice>
      ) : (
        <>
          <Card>
            <CardContent className="flex flex-col gap-3">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <h3 className="text-base font-semibold text-ks-ink">Machine state</h3>
                  <p className="mt-1 text-sm text-muted-foreground">
                    OFF blocks the next commit cycle. Live campaigns keep serving.
                  </p>
                </div>
                {/* The kill control lives here rather than in a header bar: the
                    portal owns the chrome, and the switch belongs beside the
                    state it changes. */}
                <KillToggle
                  initialState={configData.kill_state}
                  onChanged={() => void config.refetch()}
                />
              </div>
              {configData.kill_meta?.reason && (
                <p className="rounded-lg bg-muted/40 px-3.5 py-3 text-[13px] text-muted-foreground">
                  {configData.kill_meta.reason}
                </p>
              )}
            </CardContent>
          </Card>
          <DominoReorder
            order={configData.domino_order}
            registry={dominoOrder}
            thresholds={configData.domino_thresholds}
          />
        </>
      )}
    </div>
  );
}
